import { InputPartType } from "@redotech/redo-return-core-common/return-flow-answers";
import { ReturnFlow, Step } from "../../../return-flow";
import type { ReturnReason } from "./return-reasons";

export enum ReturnReasonType {
  QUESTION_ANSWER = "question_answer",
  AI_RETURN_REASONS = "ai_return_reasons",
}

export function getReturnReasons({
  multipleChoiceAnswers,
  inputAnswers,
  aiReturnReasons,
  returnFlow,
  removeMultipleChoiceNonReturnReasons,
}: {
  multipleChoiceAnswers: readonly { step: number; answer: number }[];
  inputAnswers: readonly {
    message: string;
    value: string | string[] | URL[];
    type: InputPartType;
    step?: number | undefined | null;
  }[];
  aiReturnReasons?: {
    step: number;
    aiReturnReasonsThreadId: string;
    additionalAiInfo?: string;
  };
  returnFlow: ReturnFlow;
  removeMultipleChoiceNonReturnReasons?: boolean;
}): ReturnReason[] {
  // Map multiple choice answers to their questions and response values.
  const multipleChoiceAnswers_ = multipleChoiceAnswers
    .map((choice) => {
      const stepObject = returnFlow.steps[choice.step];
      if (!stepObject || stepObject.type !== Step.MULTIPLE_CHOICE) {
        return undefined;
      }
      if (removeMultipleChoiceNonReturnReasons && !stepObject.reason) {
        return undefined;
      }
      const choiceOption = stepObject.options[choice.answer];
      if (choiceOption && choiceOption.name) {
        let additionalAiInfo = "";
        if (stepObject.additionalAiInfo) {
          additionalAiInfo += `${stepObject.additionalAiInfo}`;
        }
        if (choiceOption.additionalAiInfo) {
          additionalAiInfo += `${additionalAiInfo ? "\n" : ""}${choiceOption.additionalAiInfo}`;
        }

        return {
          type: ReturnReasonType.QUESTION_ANSWER as const,
          prompt: stepObject.message,
          answer: choiceOption.name,
          reasonCode: choiceOption.code || undefined,
          variantExchangeGuidanceForStep: additionalAiInfo || undefined,
          step: choice.step,
        };
      }
      return undefined;
    })
    .filter((answer) => answer !== undefined);

  // Filter inputs down to text answers
  const inputAnswers_ = inputAnswers
    .map((answer) => {
      if (
        answer.type === InputPartType.TEXTAREA &&
        typeof answer.value === "string"
      ) {
        return {
          type: ReturnReasonType.QUESTION_ANSWER as const,
          prompt: answer.message,
          answer: answer.value,
          reasonCode: undefined,
          // Additional ai info is currently only supported for multiple choice answers.
          variantExchangeGuidanceForStep: undefined,
          step: answer.step ?? 0,
        };
      }
      return undefined;
    })
    .filter((answer) => answer !== undefined);

  const aiReturnReasons_ = aiReturnReasons
    ? [
        {
          type: ReturnReasonType.AI_RETURN_REASONS as const,
          aiReturnReasonsThreadId: aiReturnReasons.aiReturnReasonsThreadId,
          variantExchangeGuidanceForStep:
            aiReturnReasons.additionalAiInfo || undefined,
          step: aiReturnReasons.step,
        },
      ]
    : [];

  return [...multipleChoiceAnswers_, ...inputAnswers_, ...aiReturnReasons_]
    .sort((a, b) => a.step - b.step)
    .map((response): ReturnReason => {
      if (response.type === ReturnReasonType.AI_RETURN_REASONS) {
        return {
          type: ReturnReasonType.AI_RETURN_REASONS,
          aiReturnReasonsThreadId: response.aiReturnReasonsThreadId,
          variantExchangeGuidanceForStep:
            response.variantExchangeGuidanceForStep,
        };
      }
      return {
        type: ReturnReasonType.QUESTION_ANSWER,
        prompt: response.prompt,
        answer: response.answer,
        reasonCode: response.reasonCode,
        variantExchangeGuidanceForStep: response.variantExchangeGuidanceForStep,
      };
    });
}
